import { config } from './config';

const CODE_VERIFIER_KEY = 'album_pkce_verifier';
const AUTH_STATE_KEY = 'album_auth_state';
const TOKEN_KEY = 'album_token_set_v1';

export interface TokenSet {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  expiresAt: number; // epoch seconds
  scope?: string;
  tokenType?: string;
}

export interface UserProfile {
  sub: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  picture?: string;
}

function randomString(length = 64): string {
  const array = new Uint32Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (value) => (value % 36).toString(36)).join('');
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return crypto.subtle.digest('SHA-256', data);
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function storeCodeVerifier(verifier: string): void {
  sessionStorage.setItem(CODE_VERIFIER_KEY, verifier);
}

function consumeCodeVerifier(): string | null {
  const value = sessionStorage.getItem(CODE_VERIFIER_KEY);
  if (value) {
    sessionStorage.removeItem(CODE_VERIFIER_KEY);
  }
  return value;
}

function storeAuthState(state: string): void {
  sessionStorage.setItem(AUTH_STATE_KEY, state);
}

function consumeAuthState(): string | null {
  const value = sessionStorage.getItem(AUTH_STATE_KEY);
  if (value) {
    sessionStorage.removeItem(AUTH_STATE_KEY);
  }
  return value;
}

export function getStoredTokens(): TokenSet | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as TokenSet;
    if (!parsed.accessToken || !parsed.idToken || !parsed.expiresAt) {
      return null;
    }
    return parsed;
  } catch (error) {
    console.warn('Failed to parse stored token set', error);
    return null;
  }
}

function storeTokens(tokenSet: TokenSet): void {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokenSet));
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
}

function decodeJwt(token: string): UserProfile | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const json = decodeURIComponent(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
        .split('')
        .map((char) => `%${('00' + char.charCodeAt(0).toString(16)).slice(-2)}`)
        .join('')
    );
    return JSON.parse(json) as UserProfile;
  } catch (error) {
    console.warn('Failed to decode JWT', error);
    return null;
  }
}

export function getCurrentUser(): UserProfile | null {
  const tokens = getStoredTokens();
  if (!tokens) return null;
  return decodeJwt(tokens.idToken);
}

function isTokenExpired(tokenSet: TokenSet): boolean {
  const now = Math.floor(Date.now() / 1000);
  return tokenSet.expiresAt - 60 < now;
}

async function fetchToken(params: Record<string, string>): Promise<TokenSet> {
  const body = new URLSearchParams({
    client_id: config.clientId,
    ...params
  });

  const response = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: body.toString()
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Token endpoint error (${response.status}): ${text}`);
  }

  const data = await response.json();
  const expiresIn = Number(data.expires_in ?? 3600);

  return {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: data.refresh_token,
    tokenType: data.token_type,
    scope: data.scope,
    expiresAt: Math.floor(Date.now() / 1000) + expiresIn
  };
}

export async function signIn(): Promise<void> {
  const verifier = [...crypto.getRandomValues(new Uint8Array(32))]
    .map((value) => ('0' + value.toString(16)).slice(-2))
    .join('');
  storeCodeVerifier(verifier);

  const challengeBuffer = await sha256(verifier);
  const challenge = base64UrlEncode(challengeBuffer);
  const state = randomString(24);
  storeAuthState(state);

  const authorize = new URL(`${config.cognitoDomain}/oauth2/authorize`);
  authorize.searchParams.set('client_id', config.clientId);
  authorize.searchParams.set('response_type', 'code');
  authorize.searchParams.set('scope', 'openid email profile');
  authorize.searchParams.set('redirect_uri', config.redirectUri);
  authorize.searchParams.set('code_challenge_method', 'S256');
  authorize.searchParams.set('code_challenge', challenge);
  authorize.searchParams.set('state', state);

  window.location.assign(authorize.toString());
}

export async function handleRedirectCallback(): Promise<UserProfile | null> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');

  if (!code) {
    return getCurrentUser();
  }

  const storedState = consumeAuthState();
  if (!storedState || storedState !== state) {
    throw new Error('Invalid OAuth state. Try signing in again.');
  }

  const verifier = consumeCodeVerifier();
  if (!verifier) {
    throw new Error('Missing PKCE verifier. Refresh and sign in again.');
  }

  const tokens = await fetchToken({
    grant_type: 'authorization_code',
    code,
    code_verifier: verifier,
    redirect_uri: config.redirectUri
  });

  storeTokens(tokens);

  url.searchParams.delete('code');
  url.searchParams.delete('state');
  window.history.replaceState({}, '', url.toString());

  return getCurrentUser();
}

export async function ensureValidAccessToken(): Promise<string | null> {
  const tokens = getStoredTokens();
  if (!tokens) return null;

  if (!isTokenExpired(tokens)) {
    return tokens.accessToken;
  }

  if (!tokens.refreshToken) {
    clearTokens();
    return null;
  }

  try {
    const refreshed = await fetchToken({
      grant_type: 'refresh_token',
      refresh_token: tokens.refreshToken
    });
    // fallback to previous refresh token if new not returned
    if (!refreshed.refreshToken) {
      refreshed.refreshToken = tokens.refreshToken;
    }
    storeTokens(refreshed);
    return refreshed.accessToken;
  } catch (error) {
    console.error('Failed to refresh token', error);
    clearTokens();
    return null;
  }
}

export function signOut(): void {
  const logoutUrl = new URL(`${config.cognitoDomain}/logout`);
  logoutUrl.searchParams.set('client_id', config.clientId);
  logoutUrl.searchParams.set('logout_uri', config.logoutUri);
  clearTokens();
  window.location.assign(logoutUrl.toString());
}
